import { SxProps, Theme } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import {
  DesktopTimePicker,
  DesktopTimePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDateLocale } from "core/hooks/useDateLocale";
import { InputWidthType, MUIPickersDate } from "core/types";
import { VERY_SMALL, inputWidth, margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { CSSProperties } from "react";
import { FormElementRenderProps, isValid } from "react-forms-state";
import { useTranslations } from "translations";
import { getLocaleText } from "../DatePickerInputField";
import { TextInputFieldProps } from "../TextInputField";
import { getHelperText } from "../Validation";

type PickedDesktopTimePickerProps<TDate> = Pick<
  DesktopTimePickerProps<TDate>,
  "minutesStep"
>;

type PickedTextFieldProps = Pick<TextFieldProps, "placeholder" | "required">;

type AdditionalTimePickerPresenterProps = {
  elementName: string;
  hasCustomValidation?: boolean;
  inputSx?: SxProps<Theme>;
  inputWidthType?: InputWidthType;
  label?: string;
  onChange: (value: MUIPickersDate) => void;
  textInputVariant?: TextInputFieldProps["variant"];
  timeValue?: MUIPickersDate;
  validation: any;
  value: MUIPickersDate;
  wrapperStyle?: CSSProperties;
};

type AdditionalTimePickerProps<TDate> = {
  sideMutation?: (v: TDate, mutateElement: AnyFunction) => void;
} & Omit<
  AdditionalTimePickerPresenterProps,
  "onChange" | "validation" | "value"
>;

export type TimePresenterProps<TDate = MUIPickersDate> =
  AdditionalTimePickerPresenterProps &
    PickedDesktopTimePickerProps<TDate> &
    PickedTextFieldProps;

export function TimePickerPresenter({
  elementName,
  hasCustomValidation = false,
  inputSx,
  inputWidthType = VERY_SMALL,
  label,
  minutesStep = 5,
  onChange,
  placeholder,
  required,
  textInputVariant = "standard",
  timeValue,
  validation,
  value,
  wrapperStyle,
}: TimePresenterProps<MUIPickersDate>) {
  const translations = useTranslations();
  const { currentLocale } = useDateLocale();

  return (
    <div
      style={{
        margin: margin(0),
        width: inputWidth(inputWidthType),
        ...wrapperStyle,
      }}
    >
      <LocalizationProvider
        adapterLocale={currentLocale}
        dateAdapter={AdapterDateFns}
        localeText={getLocaleText(currentLocale.code)}
      >
        <DesktopTimePicker
          ampm={false}
          minutesStep={minutesStep}
          slotProps={{
            inputAdornment: {
              position: "end",
              style: { margin: margin(0, 1, 0, 0) },
            },
            textField: {
              error: !isValid(validation),
              helperText: getHelperText({
                hasCustomValidation: !!hasCustomValidation,
                translations,
                validation,
              }),
              id: `input_text_${elementName}`,
              InputLabelProps: { style: { fontSize: FONT_SIZE_14 } },
              inputProps: {
                "data-testid": `input_text_${elementName}`,
                placeholder: placeholder,
                readOnly: false,
                style: { fontSize: FONT_SIZE_14 },
              },
              label,
              placeholder,
              required,
              sx: {
                svg: {
                  height: FONT_SIZE_16,
                  width: FONT_SIZE_16,
                },
                ...inputSx,
              },
              variant: textInputVariant,
            },
          }}
          value={timeValue ?? value}
          onChange={(newValue) => {
            if (!newValue) {
              return onChange(null);
            }

            return onChange(newValue);
          }}
        />
      </LocalizationProvider>
    </div>
  );
}

export type TimePickerProps<TDate = MUIPickersDate> =
  AdditionalTimePickerProps<TDate> &
    PickedDesktopTimePickerProps<TDate> &
    PickedTextFieldProps;

export default function TimePicker({
  elementName,
  hasCustomValidation = false,
  inputSx,
  inputWidthType = VERY_SMALL,
  label,
  minutesStep = 5,
  placeholder,
  required,
  sideMutation,
  textInputVariant = "standard",
  timeValue,
  wrapperStyle,
}: TimePickerProps<MUIPickersDate>) {
  return (
    <FormElementRenderProps
      elementName={elementName}
      sideMutation={sideMutation}
    >
      {({
        onChange,
        validation,
        value,
      }: {
        onChange: (value: MUIPickersDate) => void;
        validation: any;
        value: MUIPickersDate;
      }) => (
        <TimePickerPresenter
          elementName={elementName}
          hasCustomValidation={hasCustomValidation}
          inputSx={inputSx}
          inputWidthType={inputWidthType}
          label={label}
          minutesStep={minutesStep}
          placeholder={placeholder}
          required={required}
          textInputVariant={textInputVariant}
          timeValue={timeValue}
          validation={validation}
          value={value}
          wrapperStyle={wrapperStyle}
          onChange={onChange}
        />
      )}
    </FormElementRenderProps>
  );
}
